import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2, BarChart3, RotateCcw, Lock, DollarSign, UserPlus, BellRing, Briefcase, HeartHandshake,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import { formatCurrency } from "@/lib/format";
import { CHART_COLORS, NAVY, RED, GREEN, AMBER, PURPLE, SKY } from "./crmConstants";

const Kpi = ({ icon: Icon, label, value, color, testid }) => (
  <div className="rounded-xl border border-[#E6EDF2] bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-2">
      <Icon size={16} style={{ color }} />
      <div className="text-[11px] text-[#4B5563]">{label}</div>
    </div>
    <div className="text-2xl font-display font-bold mt-1 tabular-nums" style={{ color }}>{value}</div>
  </div>
);

const ListBlock = ({ title, items, money, testid }) => (
  <div data-testid={testid}>
    <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">{title}</div>
    {(!items || items.length === 0) ? <p className="text-[13px] text-[#9AA5B1] py-2">No data yet.</p> : (
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-center justify-between gap-2 text-[13px]">
            <span className="text-[#252221] truncate">{it.name}</span>
            <span className="tabular-nums font-semibold text-[#1C4358]">{money ? formatCurrency(it.value) : it.value}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const BarBlock = ({ items, testid, color = NAVY }) => (
  <div data-testid={testid} style={{ width: "100%", height: 210 }}>
    {(!items || items.length === 0) ? <p className="text-[13px] text-[#9AA5B1] py-2">No data yet.</p> : (
      <ResponsiveContainer>
        <BarChart data={items} margin={{ top: 6, right: 8, left: -18, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEF3F6" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#4B5563" }} interval={0} angle={-18} textAnchor="end" height={54} />
          <YAxis tick={{ fontSize: 10, fill: "#4B5563" }} allowDecimals={false} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E6EDF2" }} />
          <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    )}
  </div>
);

const PieBlock = ({ items, testid }) => (
  <div data-testid={testid} style={{ width: "100%", height: 210 }}>
    {(!items || items.length === 0) ? <p className="text-[13px] text-[#9AA5B1] py-2">No data yet.</p> : (
      <ResponsiveContainer>
        <PieChart>
          <Pie data={items} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={72} label={{ fontSize: 10 }}>
            {items.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </Pie>
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E6EDF2" }} />
        </PieChart>
      </ResponsiveContainer>
    )}
  </div>
);

const Block = ({ title, children, testid, className = "" }) => (
  <Card className={`p-4 border-[#E6EDF2] ${className}`} data-testid={testid}>
    <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">{title}</div>
    {children}
  </Card>
);

export default function CrmReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ from: "", to: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: range.from || undefined, date_to: range.to || undefined };
      const { data } = await api.get("/crm/reports", { params });
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load reports (admin only)."); }
    finally { setLoading(false); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;
  if (!data) return <div className="text-center py-16 text-[#4B5563]">Reports are available to admins only.</div>;

  const L = data.leads || {}, F = data.follow_ups || {}, O = data.opportunities || {}, R = data.revenue || {}, REL = data.relationships || {};

  return (
    <div className="pb-16" data-testid="crm-reports">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 size={22} className="text-[#1C4358]" />
          <h1 className="text-2xl font-display font-bold text-[#1C4358]">CRM Reports</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="date" data-testid="reports-date-from" value={range.from} onChange={(e) => setRange((p) => ({ ...p, from: e.target.value }))} className="h-9 w-[150px] text-[13px]" />
          <span className="text-[#9AA5B1] text-[13px]">→</span>
          <Input type="date" data-testid="reports-date-to" value={range.to} onChange={(e) => setRange((p) => ({ ...p, to: e.target.value }))} className="h-9 w-[150px] text-[13px]" />
          <Button variant="outline" size="sm" onClick={load} className="border-[#D6E0E7] text-[#1C4358] gap-1"><RotateCcw size={14} /> Refresh</Button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <Kpi icon={UserPlus} label="Total leads" value={L.total ?? 0} color={NAVY} testid="reports-kpi-leads" />
        <Kpi icon={Briefcase} label="Opportunities" value={O.total ?? 0} color={PURPLE} testid="reports-kpi-opps" />
        <Kpi icon={DollarSign} label="Pipeline value" value={formatCurrency(O.pipeline_value || 0)} color={GREEN} testid="reports-kpi-pipeline" />
        <Kpi icon={HeartHandshake} label="Relationships" value={REL.total ?? 0} color={SKY} testid="reports-kpi-rels" />
        <Kpi icon={BellRing} label="Overdue follow-ups" value={F.overdue ?? 0} color={RED} testid="reports-kpi-overdue" />
        <Kpi icon={BellRing} label="Due today" value={F.due_today ?? 0} color={AMBER} testid="reports-kpi-duetoday" />
      </div>

      {/* Leads */}
      <h2 className="text-[13px] font-bold uppercase tracking-wide text-[#1C4358] mb-2">Leads</h2>
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <Block title="Leads by source" testid="reports-leads-source"><BarBlock items={L.by_source} color={NAVY} testid="reports-leads-source-chart" /></Block>
        <Block title="Leads by service lane" testid="reports-leads-lane"><PieBlock items={L.by_service_lane} testid="reports-leads-lane-chart" /></Block>
        <Block title="Leads by stage" testid="reports-leads-stage"><BarBlock items={L.by_stage} color={SKY} testid="reports-leads-stage-chart" /></Block>
        <div className="grid grid-cols-2 gap-4">
          <Block title="By territory" testid="reports-leads-territory"><ListBlock items={L.by_territory} /></Block>
          <Block title="By urgency" testid="reports-leads-urgency"><ListBlock items={L.by_urgency} /></Block>
        </div>
      </div>

      {/* Follow-ups + Opportunities */}
      <h2 className="text-[13px] font-bold uppercase tracking-wide text-[#1C4358] mb-2">Follow-ups & Opportunities</h2>
      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <Block title="Follow-ups by assignee" testid="reports-fu-assignee"><ListBlock items={F.by_assignee} /></Block>
        <Block title="Opportunities by stage" testid="reports-opps-stage"><ListBlock items={O.by_stage} /></Block>
        <Block title="Opportunities by service lane" testid="reports-opps-lane"><ListBlock items={O.by_service_lane} /></Block>
      </div>

      {/* Revenue (internal only) */}
      <div className="flex items-center gap-2 mb-2">
        <h2 className="text-[13px] font-bold uppercase tracking-wide text-[#1C4358]">Revenue (money flow)</h2>
        <span className="flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(183,121,31,0.12)", color: "#8A5A12" }}><Lock size={11} /> Internal only</span>
      </div>
      <div className="rounded-lg px-3 py-2 text-[12px] mb-3" style={{ backgroundColor: "rgba(183,121,31,0.08)", color: "#8A5A12" }}>
        {R.note || "Compensation / money-flow formulas are placeholders and configurable later. Internal use only."}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi icon={DollarSign} label="Forecast" value={formatCurrency(R.forecast_total || 0)} color={NAVY} testid="reports-rev-forecast" />
        <Kpi icon={DollarSign} label="Pending" value={formatCurrency(R.pending_total || 0)} color={AMBER} testid="reports-rev-pending" />
        <Kpi icon={DollarSign} label="Received" value={formatCurrency(R.received_total || 0)} color={GREEN} testid="reports-rev-received" />
        <Kpi icon={Briefcase} label="Closed, no revenue" value={R.closed_no_revenue ?? 0} color={RED} testid="reports-rev-norev" />
      </div>
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <Block title="Revenue by service lane" testid="reports-rev-lane"><BarBlock items={R.by_lane} color={GREEN} testid="reports-rev-lane-chart" /></Block>
        <Block title="Revenue by territory" testid="reports-rev-territory"><ListBlock items={R.by_territory} money /></Block>
        <Block title="Revenue by opened-by contributor" testid="reports-rev-opened"><ListBlock items={R.by_opened_by} money /></Block>
        <Block title="Revenue by managed-by contributor" testid="reports-rev-managed"><ListBlock items={R.by_managed_by} money /></Block>
        <Block title="Revenue by closed-by contributor" testid="reports-rev-closed" className="md:col-span-2"><ListBlock items={R.by_closed_by} money /></Block>
      </div>

      {/* Relationships */}
      <h2 className="text-[13px] font-bold uppercase tracking-wide text-[#1C4358] mb-2">Relationships</h2>
      <div className="grid md:grid-cols-2 gap-4">
        <Block title="Relationships by type" testid="reports-rel-type"><ListBlock items={REL.by_type} /></Block>
        <Block title="Relationships by status" testid="reports-rel-status"><ListBlock items={REL.by_status} /></Block>
      </div>
    </div>
  );
}
